import React, { useState } from 'react';
import { Mail, Lock, ArrowRight, CheckCircle, X, Headphones } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toothlensLogo from "../../assets/illustrations/Toothlens Logo.jpeg";
import emailIcon from '../../assets/Icons/email.png';
import { loginAppointmentValidation, loginPasscodeValidation } from '../../Services/APIs/patient';
import { clearInsuranceSession, startInsuranceSession } from '../../Utils/insuranceSession';
import { startPhonepeSession, usesReimbursementDashboard } from '../PhonepePortal/phonepeSession';

const InsuranceLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email input, 2: Passcode input
  const [email, setEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleSendPasscode = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    // Demo email - skip API call and move to passcode screen
    if (email.toLowerCase() === 'demo@toothlens.com') {
      setIsLoading(true);
      setErrorMessage('');
      setSuccessMessage('');
      
      // Simulate loading
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSuccessMessage('Passcode sent to your email!');
      setTimeout(() => {
        setStep(2);
        setSuccessMessage('');
      }, 1000);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const formData = new FormData();
      formData.append('email', email);
      
      const response = await loginAppointmentValidation(formData);
      
      if (response.status === 200) {
        setSuccessMessage('Passcode sent to your email!');
        setTimeout(() => {
          setStep(2);
          setSuccessMessage('');
        }, 1000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to send passcode. Please try again.';
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyPasscode = async (e) => {
    e.preventDefault();
    
    if (!passcode) {
      setErrorMessage('Please enter the passcode');
      return;
    }

    if (passcode.length !== 6) {
      setErrorMessage('Passcode must be 6 digits');
      return;
    }

    // Demo email - validate with hardcoded passcode
    if (email.toLowerCase() === 'demo@toothlens.com') {
      if (passcode === '654321') {
        setIsLoading(true);
        setErrorMessage('');
        
        // Simulate loading
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Store email and demo company in localStorage
        localStorage.setItem('insuranceLoginEmail', email);
        localStorage.setItem('insuranceCompany', 'demo');
        localStorage.setItem('insuranceKycRequired', 'true');
        
        setSuccessMessage('Login successful!');
        setIsLoading(false);
        
        // Redirect to master panel
        setTimeout(() => {
          navigate('/');
        }, 1000);
        return;
      } else {
        setErrorMessage('Invalid passcode. Please try again.');
        return;
      }
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('passcode', passcode);
      
      const response = await loginPasscodeValidation(formData);
      
      if (response.status === 200) {
        const member = response.data?.data;
        // Whether a member gets Reimbursement comes from their wallet, not from the URL they signed
        // in on: members who have it go to the dashboard that offers it (the one /phonepe/login
        // uses), with that portal's session. Any earlier insurance session on this device is
        // cleared so it cannot linger behind them.
        const reimbursement = usesReimbursementDashboard(member);
        if (reimbursement) {
          clearInsuranceSession();
          startPhonepeSession(email, member);
        } else {
          // Store email, insurance company and member details in localStorage
          startInsuranceSession(email, member);
        }

        setSuccessMessage('Login successful!');

        // Redirect to the Reimbursement dashboard or the master panel
        setTimeout(() => {
          navigate(reimbursement ? '/phonepe/dashboard' : '/');
        }, 1000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Invalid passcode. Please try again.';
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendPasscode = async () => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSuccessMessage('Passcode resent to your email!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to resend passcode. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep(1);
    setPasscode('');
    setErrorMessage('');
    setSuccessMessage('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col items-center justify-center px-4 py-8 lg:py-6">
      {/* Portal intro */}
      <div className="w-full max-w-md lg:max-w-xl text-center mb-6 lg:mb-4">
        <h1 className="text-2xl font-bold text-indigo-700 mb-2">ToothLens Member Portal</h1>
        <p className="text-base font-semibold text-gray-800 mb-1">
          This portal is for registered ToothLens plan holders only.
        </p>
        <p className="text-sm text-gray-500">
          Use it to view your coverage, find network clinics and book your dental appointment.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md lg:max-w-xl overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-8 lg:px-10 lg:py-6 text-center">
          <div className="w-20 h-20 lg:w-16 lg:h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 lg:mb-3 p-2">
            <img src={toothlensLogo} alt="ToothLens Logo" className="w-full h-full object-contain rounded-full" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2 lg:mb-1">Welcome to ToothLens</h1>
          <p className="text-indigo-100 text-sm">
            {step === 1 ? 'Enter your registered email to receive your OTP' : 'Enter the passcode sent to your email'}
          </p>
        </div>

        {/* Content */}
        <div className="p-8 lg:px-10 lg:py-6">
          {step === 1 ? (
            // Step 1: Email Input
            <form onSubmit={handleSendPasscode} className="space-y-6 lg:space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrorMessage('');
                    }}
                    placeholder="Enter your email"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    disabled={isLoading}
                  />
                </div>
              </div>

              {errorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {errorMessage}
                </div>
              )}

              {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg text-sm flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {successMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-violet-700 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Send Passcode</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Your one-time passcode arrives within{' '}
                <span className="font-semibold text-gray-700">60 seconds</span> and stays valid for 10 minutes.
              </p>
            </form>
          ) : (
            // Step 2: Passcode Input
            <form onSubmit={handleVerifyPasscode} className="space-y-6 lg:space-y-4">
              <div>
                <label htmlFor="passcode" className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter Passcode
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    id="passcode"
                    value={passcode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setPasscode(value);
                      setErrorMessage('');
                    }}
                    placeholder="000000"
                    maxLength="6"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-center text-2xl tracking-widest font-semibold"
                    disabled={isLoading}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Passcode sent to <span className="font-semibold text-gray-700">{email}</span>
                </p>
              </div>

              {errorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {errorMessage}
                </div>
              )}

              {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg text-sm flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {successMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-violet-700 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <span>Verify & Login</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <div className="flex items-center justify-between  text-sm">
                {/* <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="text-gray-600 hover:text-gray-800 font-medium"
                  disabled={isLoading}
                >
                  ← Change Email
                </button> */}
                <button
                  type="button"
                  onClick={handleResendPasscode}
                  className="text-indigo-600 hover:text-indigo-800 font-medium"
                  disabled={isLoading}
                >
                  Resend Code
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 lg:px-10 py-4 lg:py-3 text-center text-xs text-gray-600">
          <p>
            By continuing, you agree to{' '}
            <button
              type="button"
              onClick={() => setShowTermsModal(true)}
              className="text-indigo-600 hover:text-indigo-800 font-semibold underline"
            >
              Terms of Service
            </button>
            {' '}and{' '}
            <button
              type="button"
              onClick={() => setShowPrivacyModal(true)}
              className="text-indigo-600 hover:text-indigo-800 font-semibold underline"
            >
              Privacy Policy
            </button>
          </p>
          <p className="mt-2">
            Trouble signing in?{' '}
            <button
              type="button"
              onClick={() => setShowSupportModal(true)}
              className="text-indigo-600 hover:text-indigo-800 font-semibold underline"
            >
              Contact onboarding support
            </button>
          </p>
        </div>
      </div>

      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-violet-600 p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Terms of Service</h3>
              <button onClick={() => setShowTermsModal(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-6 h-6 text-white" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)] text-sm text-gray-700 leading-relaxed space-y-4">
              <p>
                By signing up, you agree to share your name, mobile number, email, basic medical/dental details, 
                and AI dental scan images with Toothlens to generate your scan report and enable appointment booking 
                at Toothlens Dental Network clinics.
              </p>
              <p>
                Toothlens AI provides indicative insights only and does not replace a dentist's diagnosis. 
                All treatments are determined at the consulting clinic.
              </p>
              <p>
                Your data will be handled confidentially and used only to provide our services. 
                By continuing, you accept these Terms & Conditions and our privacy practices.
              </p>
            </div>
            <div className="sticky bottom-0 bg-gray-50 p-4">
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-2 rounded-lg font-semibold hover:from-indigo-700 hover:to-violet-700 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-violet-600 p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Privacy Policy</h3>
              <button onClick={() => setShowPrivacyModal(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-6 h-6 text-white" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)] text-sm text-gray-700 leading-relaxed space-y-4">
              <p>
                By signing up, you agree to share your name, mobile number, email, basic medical/dental details, 
                and AI dental scan images with Toothlens to generate your scan report and enable appointment booking 
                at Toothlens Dental Network clinics.
              </p>
              <p>
                Toothlens AI provides indicative insights only and does not replace a dentist's diagnosis. 
                All treatments are determined at the consulting clinic.
              </p>
              <p>
                Your data will be handled confidentially and used only to provide our services. 
                By continuing, you accept these Terms & Conditions and our privacy practices.
              </p>
            </div>
            <div className="sticky bottom-0 bg-gray-50 p-4">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-2 rounded-lg font-semibold hover:from-indigo-700 hover:to-violet-700 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Support Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Headphones className="w-5 h-5 text-white" />
                <h3 className="text-lg font-bold text-white">Onboarding Support</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowSupportModal(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-3">
              <a
                href="mailto:onboarding@toothlens.com"
                className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <img src={emailIcon} alt="Email" className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Email</p>
                  <p className="text-gray-800 font-semibold text-sm">onboarding@toothlens.com</p>
                </div>
              </a>
            </div>

            <div className="bg-gray-50 p-4">
              <button
                type="button"
                onClick={() => setShowSupportModal(false)}
                className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-2 rounded-lg font-semibold hover:from-indigo-700 hover:to-violet-700 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsuranceLogin;